import React, { useState, useEffect } from "react";
import { db, auth } from "../services/firebase";
import { ref, set } from "firebase/database";
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
} from "firebase/auth";
import "./TeacherRegistration.css";

function TeacherRegistration() {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    college: "",
    designation: "",
    message: "",
    verificationCode: "",
    idCardLink: "",
    aadharCardLink: "",
  });

  const [status, setStatus] = useState("");
  const [captcha, setCaptcha] = useState("");
  const [generatedPassword, setGeneratedPassword] = useState("");
  const [submittedEmail, setSubmittedEmail] = useState(""); // store email for alert

  // Generate captcha
  const generateCaptcha = () => {
    const chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let code = "";
    for (let i = 0; i < 6; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCaptcha(code);
  };

  useEffect(() => {
    generateCaptcha();
  }, []);

  // Handle form input
  const onChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // Generate random password
  const generatePassword = () => {
    return Math.random().toString(36).slice(-10); // 10-char password
  };

  // Validate phone number format
  const isValidPhone = (phone) => {
    const phoneRegex = /^\+?[0-9]{10,15}$/; 
    return phoneRegex.test(phone);
  };

  // Validate Google Drive link
  const isValidDriveLink = (link) => {
    return /^https:\/\/drive\.google\.com/.test(link);
  };

  // Submit
  const onSubmit = async (e) => {
    e.preventDefault();

    if (form.verificationCode !== captcha) {
      setStatus("❌ Verification code incorrect.");
      generateCaptcha();
      return;
    }

    if (!isValidPhone(form.phone)) {
      setStatus("❌ Please enter a valid phone number.");
      return;
    }

    if (!isValidDriveLink(form.idCardLink) || !isValidDriveLink(form.aadharCardLink)) {
      setStatus("❌ Please provide valid Google Drive links.");
      return;
    }

    try {
      const password = generatePassword();
      setGeneratedPassword(password);
      setSubmittedEmail(form.email); // store email for alert

      const registrationDate = new Date();
      const formattedDate = registrationDate.toLocaleString();

      // 1️⃣ Create Firebase Auth user
      const result = await createUserWithEmailAndPassword(auth, form.email, password);
      const user = result.user;

      // 2️⃣ Send verification email
      await sendEmailVerification(user);

      // 3️⃣ Save teacher data
      const teacherRef = ref(db, "teacherRegistrations/" + user.uid);
      await set(teacherRef, {
        ...form,
        uid: user.uid,
        generatedPassword: password,
        verified: false,
        status: "pending",
        submittedAt: registrationDate.toISOString(), // backend-friendly
        registrationDate: formattedDate,             // human-readable
      });

      setStatus(`✅ Registration submitted on ${formattedDate}! Please check your email (${form.email}) for verification.`);

      // Reset form
      setForm({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        college: "",
        designation: "",
        message: "",
        verificationCode: "",
        idCardLink: "",
        aadharCardLink: "",
      });
      generateCaptcha();
    } catch (error) {
      setStatus("❌ Failed: " + error.message);
    }
  };

  return (
    <div className="tl-registration">
      <h2 className="tl-title">👩‍🏫 Teacher Registration</h2>
      <form onSubmit={onSubmit} className="tl-form">
        <div className="tl-form-row">
          <label className="tl-label">
            First Name *
            <input
              type="text"
              name="firstName"
              value={form.firstName}
              onChange={onChange}
              required
              className="tl-input"
            />
          </label>
          <label className="tl-label">
            Last Name *
            <input
              type="text"
              name="lastName"
              value={form.lastName}
              onChange={onChange}
              required
              className="tl-input"
            />
          </label>
        </div>

        <label className="tl-label">
          Email *
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={onChange}
            required
            className="tl-input"
          />
        </label>

        <label className="tl-label">
          Phone (with country code) *
          <input
            type="tel"
            name="phone"
            placeholder="+91XXXXXXXXXX"
            value={form.phone}
            onChange={onChange}
            required
            className="tl-input"
          />
        </label>

        <label className="tl-label">
          College *
          <input
            type="text"
            name="college"
            value={form.college}
            onChange={onChange}
            required
            className="tl-input"
          />
        </label>

        <label className="tl-label">
          Designation *
          <input
            type="text"
            name="designation"
            value={form.designation}
            onChange={onChange}
            required
            className="tl-input"
          />
        </label>

        <label className="tl-label">
          Message *
          <textarea
            name="message"
            rows="4"
            value={form.message}
            onChange={onChange}
            required
            className="tl-textarea"
          />
        </label>

        {/* Captcha */}
        <div className="tl-captcha-box">
          <div className="tl-captcha-display">{captcha}</div>
          <button type="button" onClick={generateCaptcha} className="tl-refresh-btn">
            🔄 Refresh
          </button>
        </div>

        <label className="tl-label">
          Verification Code *
          <input
            type="text"
            name="verificationCode"
            value={form.verificationCode}
            onChange={onChange}
            required
            className="tl-input"
          />
        </label>

        <label className="tl-label">
          Google Drive Link – ID Card *
          <input
            type="url"
            name="idCardLink"
            value={form.idCardLink}
            onChange={onChange}
            required
            className="tl-input"
          />
        </label>

        <label className="tl-label">
          Google Drive Link – Aadhaar Card *
          <input
            type="url"
            name="aadharCardLink"
            value={form.aadharCardLink}
            onChange={onChange}
            required
            className="tl-input"
          />
        </label>

        <button type="submit" className="tl-submit-btn">
          Submit Registration
        </button>
      </form>

      {/* Alerts */}
      <div className="tl-alert-container">
        {status && (
          <div
            className={`tl-form-alert ${
              status.startsWith("✅") ? "tl-success" : "tl-error"
            }`}
          >
            {status}
          </div>
        )}

        {generatedPassword && submittedEmail && (
          <div className="tl-form-alert tl-info">
            Save this password: <b>{generatedPassword}</b> <br />
            Use it with your email <b>{submittedEmail}</b> to log in after verification.
          </div>
        )}
      </div>
    </div>
  );
}

export default TeacherRegistration;
